import { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { PERIOD_BUCKETS, normalizePeriodBuckets } from '../../utils/periodizationPeriodBuckets';
import {
  effectiveBucketsForType,
  parseScopeOverrides,
} from '../../utils/periodizationPlanningScope';

function attrLabel(t, typeKey, fallback) {
  const k = `periodization.attributes.${typeKey}`;
  const tr = t(k);
  return tr !== k ? tr : fallback || typeKey;
}

export default function PlanningParametersModal({ open, onOpenChange, tree, onUpdated }) {
  const { t } = useLanguage();
  const allTypes = tree?.assessment_set?.attribute_types || [];
  const block = tree?.block;
  const [scopeOverrides, setScopeOverrides] = useState(() =>
    parseScopeOverrides(block?.planning_scope_overrides)
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setScopeOverrides(parseScopeOverrides(block?.planning_scope_overrides));
    }
  }, [open, block?.planning_scope_overrides]);

  const toggleTypeDisabled = (typeKey, disabled) => {
    setScopeOverrides((prev) => {
      const set = new Set(prev.disabled_types || []);
      if (disabled) set.add(typeKey);
      else set.delete(typeKey);
      return { ...prev, disabled_types: [...set] };
    });
  };

  const toggleBucket = (typeKey, bucket, on) => {
    setScopeOverrides((prev) => {
      const type = allTypes.find((x) => x.type_key === typeKey);
      const current = normalizePeriodBuckets(
        prev.type_buckets?.[typeKey] || type?.period_buckets
      );
      return {
        ...prev,
        type_buckets: {
          ...(prev.type_buckets || {}),
          [typeKey]: { ...current, [bucket]: on },
        },
      };
    });
  };

  const save = async () => {
    if (!block?.id) return;
    setSaving(true);
    try {
      const res = await api.put(`/api/training/periodization/blocks/${block.id}`, {
        planning_scope_overrides: scopeOverrides,
      });
      onUpdated?.(res.data);
      onOpenChange(false);
    } catch (e) {
      window.alert(e.response?.data?.error || t('periodization.planScope.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-[90vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{t('periodization.planScope.configureParameters')}</DialogTitle>
          <DialogDescription>{t('periodization.planScope.configureParametersHint')}</DialogDescription>
        </DialogHeader>

        <div className='overflow-x-auto'>
          <table className='w-full min-w-[480px] border-collapse text-xs'>
            <thead>
              <tr className='border-b border-border text-left text-muted-foreground'>
                <th className='py-2 pr-2 font-medium'>{t('periodization.planScope.parameter')}</th>
                <th className='px-2 py-2 font-medium'>{t('periodization.planScope.inPlan')}</th>
                {PERIOD_BUCKETS.map((b) => (
                  <th key={b} className='px-2 py-2 font-medium capitalize'>
                    {t(`periodization.planScope.buckets.${b}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allTypes.map((type) => {
                const disabled = scopeOverrides.disabled_types?.includes(type.type_key);
                const buckets = effectiveBucketsForType(type, scopeOverrides) || normalizePeriodBuckets({});
                const allowed = normalizePeriodBuckets(type.period_buckets_allowed || type.period_buckets);
                return (
                  <tr key={type.type_key} className={cn('border-b border-border/60', disabled && 'opacity-50')}>
                    <td className='py-2 pr-2 font-medium'>{attrLabel(t, type.type_key, type.name)}</td>
                    <td className='px-2 py-2'>
                      <Switch
                        checked={!disabled}
                        onCheckedChange={(on) => toggleTypeDisabled(type.type_key, !on)}
                        aria-label={t('periodization.planScope.inPlan')}
                      />
                    </td>
                    {PERIOD_BUCKETS.map((bucket) => (
                      <td key={bucket} className='px-2 py-2 text-center'>
                        {allowed[bucket] ? (
                          <input
                            type='checkbox'
                            className='h-3.5 w-3.5 accent-primary'
                            checked={!!buckets[bucket]}
                            disabled={disabled}
                            onChange={(e) => toggleBucket(type.type_key, bucket, e.target.checked)}
                          />
                        ) : (
                          <span className='text-muted-foreground/40'>—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <DialogFooter>
          <Button type='button' variant='ghost' onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type='button' onClick={save} disabled={saving}>
            {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
